import React, { Fragment, useCallback, useMemo, lazy, Suspense } from 'react';
import Div from '@/shared/Div';
import Button from '@/shared/Button';
import RightArrow from '../Icons/RightArrow';
import Link from '@/shared/Link';
import { getAddress } from '@/utils/address';
import DeviceStatus from './DeviceStatus';
import Header from '@/shared/Header';
import Paragraph from '@/shared/Paragraph';
import { useServiceContext } from '@/context/serviceRequestContext';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import Image from 'next/image';
import Map from '../Map';
import { ADDITIONAL_PLANS, MY_ORDERS_TAB_URL } from '@/configs/constants';
import useRoutingLoader from '@/hooks/useRoutingLoader';
import useGTM from '@/hooks/useGTM';
import { GTMEventNames, SCREEN_NAMES } from '@/types/gtm';
import { useProductContext } from '@/context/productContext';
import is from '@/utils/is';
import {
  AMC_NAME,
  AMC_PLAN_TYPE_NAMES,
  DEFAULT_DEVICE_FAMILY_NAME,
  PLAN_BORDER_MAP,
} from '@/constants/amc';
import date from '@/utils/date';
import { blockAMCJourney } from '@/utils/amcPlans';
import ExtendedWarrantyCard from '../AMC/ExtendedWarrantyCard';
import Modal from '@/shared/crisp/Modal';
const DeviceServiceBlock = lazy(() => import('./DeviceServiceBlock'));
import useModalToggle from '@/hooks/useModalToggle';
import { getAssetServiceBookingURL } from '@/utils/urls';

type Address = {
  house_number: string;
  address_name: string;
  address1: string;
  address2: string;
  address3: string;
  address4: string;
  city: string;
  state: string;
  postal_code: string;
  latitude: string;
  longitude: string;
};

type Plan = {
  agreementId: string;
  startDate: string;
  endDate: string;
};

type DeviceProps = {
  name: string;
  id: string;
  address: Address;
  planType: string;
  currentPlan: Plan;
  expiryStatus: any;
  deviceId: string;
  isRental?: boolean;
  image?: string;
  upcomingAMCPlan?: any;
  className?: string;
  isOpenServiceRequest?: boolean;
  savingPromotionData?: any;
  deviceFamilyName?: any;
  showBookServiceCta?: boolean;
};

type AdditonalPlan = {
  agreementId: string;
  startDate: string;
  endDate: string;
  label: string;
  planType: string;
};

type StatusType = keyof typeof COHORT_CTAS;

const COHORT_CTAS = {
  IC: {
    id: 'renew-amc-cta',
    label: 'Renew AMC',
  },
  IW: {
    id: 'get-amc-cta',
    label: 'Get AMC',
  },
  OC: {
    id: 'renew-amc-cta',
    label: 'Renew AMC',
  },
  OW: {
    id: 'get-amc-cta',
    label: 'Get AMC',
  },
};

const renderUpcomingPlan = ({ upcomingAMCPlan }: any) => {
  const { endDate, startDate, planType } = upcomingAMCPlan;

  if (is.empty.string(endDate) || is.empty.string(startDate) || is.empty.string(planType))
    return null;
  return (
    <Div
      className={classNames('flex flex-col py-4 px-6 border-y', {
        [PLAN_BORDER_MAP[planType]]: true,
      })}
    >
      <Header size="sm" className={classNames('text-core-text-icons-primary')}>
        {AMC_NAME} {AMC_PLAN_TYPE_NAMES[planType?.toUpperCase()]}
      </Header>

      <Paragraph className={classNames('text-black/50')}>
        {`${date
          .format(startDate, {
            month: 'short',
            day: '2-digit',
          })
          .replaceAll('-', ' ')}- ${date.format(endDate, {
          month: undefined,
          day: undefined,
        })}`}
      </Paragraph>
    </Div>
  );
};

function DeviceCard({
  name,
  id,
  address,
  deviceId,
  planType,
  currentPlan,
  expiryStatus,
  isRental,
  image,
  className,
  isOpenServiceRequest,
  upcomingAMCPlan,
  savingPromotionData = {},
  deviceFamilyName,
  showBookServiceCta = true,
}: DeviceProps) {
  const status = expiryStatus?.status as StatusType;

  const { dispatch: serviceAction } = useServiceContext();
  const { state: productState } = useProductContext();
  const router = useRouter();
  const { fireGTMEvent } = useGTM();
  const { routing, setRouting } = useRoutingLoader();
  const { open, openModal, closeModal } = useModalToggle(false);

  const extendedWarrantyPlans = useMemo(() => {
    return productState?.selectedProduct?.additionalPlans?.filter(
      ({ planType }: any) => planType === ADDITIONAL_PLANS.EXTENDED_WARRANTY,
    );
  }, [productState?.selectedProduct]);

  const bookServiceHandler = useCallback(() => {
    const redirectChannel = productState?.selectedProduct?.redirectChannel;
    fireGTMEvent(GTMEventNames.BOOK_SERVICE_CLICKED, {
      ms_open: false,
      sr_open: false,
      screen_name: SCREEN_NAMES.DEVICE_HOMEPAGE,
      redirectChannel,
    });
    if (
      !productState?.selectedProduct?.isRobo &&
      redirectChannel &&
      ['CALL_CENTRE', 'CUSTOMER_APP'].includes(redirectChannel)
    ) {
      openModal();
      return;
    }

    if (productState?.selectedProduct?.isRobo) {
      router
        .push({
          pathname: getAssetServiceBookingURL(id),
        })
        .then(() => setRouting(false));
      return;
    }

    serviceAction.changeComponent('bookAService', true);
    setRouting(true);
    router
      .push({
        pathname: `/service/${id}`,
      })
      .then(() => setRouting(false));
  }, [router, serviceAction, id, setRouting, fireGTMEvent, productState?.selectedProduct?.isRobo]);

  const buyAMCHandler = useCallback(() => {
    const isRenewAMCCTA = Boolean(document.getElementById('renew-amc-cta'));
    const eventName = isRenewAMCCTA
      ? GTMEventNames.RENEW_AMC_CLICKED
      : GTMEventNames.GET_AMC_CLICKED;
    setRouting(true);
    fireGTMEvent(eventName, {
      screen_name: SCREEN_NAMES.DEVICE_HOMEPAGE,
    });
    router
      .push({
        pathname: `/amc-plans/${id}/${deviceId}`,
        query: {
          assets: true,
          deviceFamilyName: deviceFamilyName ?? DEFAULT_DEVICE_FAMILY_NAME,
        },
      })
      .then(() => setRouting(false));
  }, [id, deviceId, setRouting, router, fireGTMEvent, deviceFamilyName]);

  const renewRentalSubscription = useCallback(() => {
    fireGTMEvent(GTMEventNames.RENEW_RENTAL_CLICKED, {
      screen_name: SCREEN_NAMES.DEVICE_HOMEPAGE,
    });
    router.push({
      pathname: MY_ORDERS_TAB_URL,
    });
  }, [router, fireGTMEvent]);
  const ctas = useMemo(() => {
    let ctas = [];
    // Dont show AMC CTA Condtion
    const hideAMCCTA = blockAMCJourney(productState?.selectedProduct);
    if (!hideAMCCTA) {
      const { label: type, id } = COHORT_CTAS[status] ?? {};
      ctas.push({
        id,
        type,
        onClickHandler: buyAMCHandler,
      });
    }
    if (isRental) {
      ctas.push({
        id: 're-new-subscription-cta',
        type: 'Renew subscription',
        onClickHandler: renewRentalSubscription,
      });
    }
    if (showBookServiceCta) {
      if (productState?.selectedProduct?.isRobo) {
        ctas.push({
          id: 'book-service-cta',
          type: 'Get Assistance',
          onClickHandler: bookServiceHandler,
        });
      } else {
        ctas.push({
          id: 'book-service-cta',
          type: 'Book Service',
          onClickHandler: bookServiceHandler,
        });
      }
    }
    return ctas;
  }, [
    productState?.selectedProduct,
    isRental,
    status,
    buyAMCHandler,
    renewRentalSubscription,
    bookServiceHandler,
    showBookServiceCta,
  ]);

  const renderExtendWarrantyCard = ({ startDate, endDate, label }: AdditonalPlan) => {
    const planDuration = date.getDifferenceInYears(startDate, endDate);
    return <ExtendedWarrantyCard endDate={endDate} planDuration={planDuration} planTitle={label} />;
  };

  return (
    <Fragment>
      <Div
        className={classNames(
          'flex flex-col border border-neutral-50 gap-s6 py-6 font-primary bg-white shadow-medium rounded-cs',
          className,
        )}
      >
        <Image
          loading="eager"
          priority={true}
          src={image ?? ''}
          height={121}
          width={121}
          alt={name}
          className="object-contain h-full aspect-square"
          data-testid="device-image"
        />
        <Div className="flex flex-col px-6  gap-s3">
          <Link
            href={isRental ? MY_ORDERS_TAB_URL : `/service?assetid=${id}&showMyDevices=true`}
            className="flex gap-s2 items-center justify-between"
          >
            <Header className="truncate text-black/70">{name}</Header>
            <RightArrow />
          </Link>
          <Paragraph className="text-black/50"> {getAddress(address)}</Paragraph>
        </Div>
        <Div className="flex flex-col gap-2">
          <DeviceStatus
            currentPlan={currentPlan}
            planType={planType}
            expiryStatus={expiryStatus}
            isRental={isRental}
            isOpenServiceRequest={isOpenServiceRequest}
            numberOfCtas={ctas.length}
            savingPromotionData={savingPromotionData}
            upcomingAMCPlan={upcomingAMCPlan}
          />
          {!isRental && !is.empty.string(upcomingAMCPlan?.agreementId)
            ? renderUpcomingPlan({ upcomingAMCPlan })
            : null}

          {is.not.empty.array(extendedWarrantyPlans) ? (
            <Map
              className="-mt-2 flex flex-col gap-s2"
              list={extendedWarrantyPlans}
              render={renderExtendWarrantyCard}
            />
          ) : null}
        </Div>

        <Map
          className="px-6 flex flex-col gap-s5"
          list={ctas}
          render={({ type, id, onClickHandler }, index) => (
            <Button
              id={id}
              key={type}
              onClick={onClickHandler}
              className="w-full"
              isLoading={routing}
              theme={index === 0 && ctas.length > 1 ? 'secondary' : 'primary'}
            >
              {type}
            </Button>
          )}
        />
      </Div>
      <Modal open={open} onClose={closeModal} showCloseButton={true} classNames="md:w-[491px]">
        <Suspense fallback={null}>
          <DeviceServiceBlock
            onClose={closeModal}
            redirectChannel={productState?.selectedProduct?.redirectChannel}
            assetId={id}
          />
        </Suspense>
      </Modal>
    </Fragment>
  );
}

export default DeviceCard;
